// The join URL, parsed. One pure function, because "which screen is this device" is the single
// most consequential decision the shell makes and it has to be verifiable without a browser.
//
// The format (session/contracts/p2-access.md, docs/access.md):
//
//   <origin>/#/join?project=<projectId>&pk=<projectViewerKey>&sk=<sessionShareKey>
//
// Both bearer keys ride in the FRAGMENT, never the path or the query: a fragment is not sent to a
// server, so a key cannot land in a gateway access log or a referrer header. The role is not in the
// URL at all and never will be — it comes from the registry lookup on the hash of `sk`, so a
// curious coach cannot edit their way onto the mover screen (docs/access.md).

// What the QR carries. `sk` decides the role, `pk` only buys the registry read.
export interface JoinParams {
  readonly projectId: string;
  readonly pk: string;
  readonly sk: string;
}

// What the address bar carries AFTER a successful join, and what a reload joins back off. No key
// anywhere in it: `h` is the sha256 the REGISTRY is keyed on, useful only to a device whose
// identity already holds the grants the join redeemed. Pasting it on another device buys a 403.
export interface ResumeParams {
  readonly projectId: string;
  readonly codeHash: string;
}

export type Route =
  | { readonly kind: 'join'; readonly params: JoinParams }
  // A reload of a device that already joined: re-derive the role from the registry off the stored
  // hash, redeeming nothing. The role STILL comes from the registry and never from the URL.
  | { readonly kind: 'resume'; readonly params: ResumeParams }
  // The owner device's own entry. Not a share redemption: the admin CREATED the project, so it is
  // already the owner and has nothing to redeem (docs/user-journeys.md, the admin journey).
  | { readonly kind: 'admin' }
  // A join link missing one of its three parts. Named rather than folded into `none`, because a
  // half-copied link is a thing a human did and deserves to be told about.
  | { readonly kind: 'badJoin' }
  | { readonly kind: 'none' };

export function parseRoute(path: string): Route {
  const hash = path.indexOf('#');
  if (hash < 0) return { kind: 'none' };
  const fragment = path.slice(hash + 1);
  const query = fragment.indexOf('?');
  const route = query < 0 ? fragment : fragment.slice(0, query);
  if (route === '/admin') return { kind: 'admin' };
  const params = new URLSearchParams(query < 0 ? '' : fragment.slice(query + 1));
  if (route === '/resume') {
    const projectId = params.get('project') ?? '';
    const codeHash = params.get('h') ?? '';
    // A half resume URL gets the same screen as a half join URL: it is a thing a human edited or
    // truncated, and the fix is the same — scan the code, not the address bar.
    if (projectId === '' || codeHash === '') return { kind: 'badJoin' };
    return { kind: 'resume', params: { projectId, codeHash } };
  }
  if (route !== '/join') return { kind: 'none' };
  const projectId = params.get('project') ?? '';
  const pk = params.get('pk') ?? '';
  const sk = params.get('sk') ?? '';
  if (projectId === '' || pk === '' || sk === '') return { kind: 'badJoin' };
  return { kind: 'join', params: { projectId, pk, sk } };
}

// Where a successful join leaves the address bar: refresh-proof, key-free. `replace`d over the
// join URL so no history entry ever holds the keys (see pathWithoutKeys below for why replace).
export function resumePath(params: ResumeParams): string {
  return `/#/resume?project=${encodeURIComponent(params.projectId)}&h=${encodeURIComponent(params.codeHash)}`;
}

// What a redacted bearer key leaves behind. Fixed rather than length-preserving or hashed: a log
// line should show that a key WAS there without showing anything derived from it, and a leak scan
// wants one string to grep for.
export const REDACTED_KEY = '<redacted>';

// The same string with every bearer key value replaced by that marker. Not a parser: it takes any
// string that might have a join URL somewhere in it — a log message, a trace field, a narrated
// payload — and blanks the two parameters that must never be written down (docs/access.md).
//
// This belongs at the LOG AND TRACE boundaries only, never on the path itself. AppStore has to keep
// the real URL: it is the thing the keys are read out of to join with. Redacting at the source
// would break the join; redacting at the sink is what keeps the key out of the write-down.
//
// `pk`/`sk` are matched only after a `?` or `&`, so a parameter that merely ENDS in them is left
// alone, and the value stops at the first delimiter, so a URL embedded in JSON does not swallow the
// rest of the line.
export function redactKeys(text: string): string {
  return text.replace(/([?&](?:pk|sk)=)[^&"'\s]*/g, `$1${REDACTED_KEY}`);
}

// The same location with the fragment gone, for the `replaceState` that takes the bearer keys out
// of the address bar once they have been redeemed. `replace`, not `push`: a history entry holding a
// live share key is a key that survives the back button and every screenshot of it.
export function pathWithoutKeys(path: string): string {
  const hash = path.indexOf('#');
  const base = hash < 0 ? path : path.slice(0, hash);
  return base === '' ? '/' : base;
}
